import axios from 'axios'
import utils from '@/plugins/utils'

// API endpoint constants
const API_ENDPOINTS = {
  CV: '/cvs/',
  STUDENT_REVIEW: '/student-reviews/',
  ACTIVE_CODE: '/active-codes/',
  AUTH: '/auth/local',
  USER: '/users/',
  UPLOAD: '/upload/',
  DESTROY: '/upload/files/',
  NOTIFICATION: '/posts/',
  LEAD: '/leads/',
  LOG: '/logs/',
  USER_LOG: '/user-logs',
  APPOINTMENT: '/appointments/',
  PRODUCT: '/products/',
  ROLE: '/users-permissions/roles',
  PRODUCT_TYPE: '/product-types/',
  STATUS_COUNT: '/countStatus/',
  SUCCEED_COUNT: '/countSucceed/',
  POLICIES: '/policies/',
  EXAM: '/exams/',
  SUBJECT: '/subjects/',
  ROOM: '/rooms/',
  STUDENT: '/students/',
  STUDENT_EXAM: '/student-exams/',
  MARK: '/marks/',
  TEACHER: '/teachers/',
  TEACHER_EXAM: '/teacher-exams/',
  MONITOR_EXAM: '/monitor-exams/',
  ROOM_SUBJECT: '/room-subjects/',
  SUBJECT_GROUP: '/subject-groups/',
  EXAM_ROOM: '/exam-rooms/',
  INDEXING: '/indexing-progresses/',
  SMS: '/sent-sms/',
  EMAIL: '/sent-emails/',
  INFO_PROGRESS: '/info-progresses/',
  LEAD_PROGRESS: '/lead-progresses/',
  STUDENT_QUEUE: '/student-queues/',
  FORM_QUEUE: '/form-queues/',
  ENROLL_FORM: '/enroll-forms/',
  ENROLL_CLASS: '/enroll-classes/',
  STUDENT_SERVICES_FORM: '/student-services-forms/',
  BASE_GRADE: '/base-grade/',
  PAGE_SETTING: '/page-settings/',
  NEWS: '/enroll-news/',
  ENROLL_SLOT: '/enroll-slots/',
  EVENT: '/events/',
  INTERVIEW: '/interviews/',
  INTERVIEW_COUNCIL: '/interview-councils/',
  INTERVIEW_SLOT: '/interview-slots/',
  INTERVIEW_INVITATION: '/interview-invitations/',
  ENROLL_NUMBER: '/enroll-number',
  RULE_CONFIG: '/rule-configs/',
  REFUND_FORM: '/refund-forms',
  PAGE_SETTING_REVISION: '/page-setting-revisions/',
  PAGE_SETTING_VALUE: '/page-setting-values/',
  USER_ADMIN: '/users-admin/',
  USER_STAT: '/user-stat/',
  DASHBOARD_ANALYTIC: '/dashboard-analytics/',
  COUNTER: '/counter',
  ENROLL_CHANGE_REQUEST: '/enroll-change-request/',
  EXAM_ROUND: '/exam-round/',
  STEP_STUDENT_ACCESS: '/step-student-access/',
  ENROLLMENT_WINDOW: '/enrollment-windows/',
  FORM_FIELD_DEFINITION: '/form-field-definitions/',
  APPLICATION_FEE: '/application-fee/',
  MESSAGE_TEMPLATE: '/message-template/',
  CV_TRANSACTION: '/cv-transactions/',
  ADMISSION_METHOD: '/admission-methods/',
  ADMISSION_CYCLE: '/admission-cycles',
  ADMISSION_FORMULA: '/admission-formulas/',
  ADMISSION_VARIABLE: '/admission-variables/',
  ADMISSION_PLAN: '/admission-plans/',
  ADMISSION_PASSING_OPTION: '/admission-passing-options/',
  ADMISSION_EVALUATION: '/admission-evaluations/',
  ADMISSION_EVALUATION_BATCH: '/admission-evaluation-batch/',
  SURVEY: '/surveys/',
  STUDENT_SURVEY: '/student-surveys/',
  SURVEY_QUESTION: '/survey-questions/'
}

// Helper function to create standard API methods
const createAPIHelper = api => ({
  search: (params, option) => axios.get(api, {params: utils.filterObject(params)}, option),
  count: (params, option) => axios.get(`${api}count`, {params: utils.filterObject(params)}, option),
  fetch: (params, option) => axios.get(api, {params: utils.filterObject(params)}, option),
  fetchOne: (id, option) => axios.get(`${api}${id}`, option),
  create: (params, options) => axios.post(api, utils.filterObject(params), options),
  update: (id, params, option) => axios.put(`${api}${id}`, utils.filterObject(params), option),
  remove: (id, option) => axios.delete(`${api}${id}`, option)
})

// Export API Repository helper
export const APIRespository = createAPIHelper

// Create API modules
export const BaseGrade = {
  ...createAPIHelper(API_ENDPOINTS.BASE_GRADE)
}

export const EnrollNumber = {
  ...createAPIHelper(API_ENDPOINTS.ENROLL_NUMBER),
  update: (id, params, option) => axios.put(API_ENDPOINTS.ENROLL_NUMBER, utils.filterObject(params), option),
  statistics: data => axios.get('/enroll-number/statistics', data)
}

export const EnrollSlot = {
  ...createAPIHelper(API_ENDPOINTS.ENROLL_SLOT),
  // Deprecated - ZALO_PAY: use Transaction.payEnrollSlot / Transaction.getMyEnrollSlot
  reserveEnrollSlot: data => axios.post('/enroll-slots/reserve', data),
  // Deprecated - ZALO_PAY
  checkEnrollPaymentStatus: data => axios.post('/enroll-slots/check-payment-status', data),
  checkValidExpectation: data => axios.post('/enroll-slots/check-valid-expectation', data),
  updateExpectation: data => axios.post('/enroll-slots/update-expectation', data),
  cancelEnrollSlot: data => axios.post('/enroll-slots/cancel', data),
  adminUpdateExpectation: data => axios.post('/enroll-slots/admin-update-expectation', data),
  // Deprecated - ZALO_PAY (no BIDV equivalent yet; kept for the admin reserve flow)
  adminReserveEnroll: data => axios.post('/enroll-slots/admin-reserve', data),
  // Deprecated - ZALO_PAY
  adminCheckEnrollPaymentStatus: data => axios.post('/enroll-slots/admin-check-payment-status', data),
  // Deprecated - ZALO_PAY
  adminConfirmChanged: data => axios.post('/enroll-slots/adminConfirmChanged', data)
}

export const EnrollmentWindow = {
  ...createAPIHelper(API_ENDPOINTS.ENROLLMENT_WINDOW)
}

export const Auth = {
  ...createAPIHelper(API_ENDPOINTS.AUTH),
  signIn: params => axios.post('/auth/local', utils.filterObject(params), {skipAuth: true}),
  signInAdmin: params => axios.post('/auth/log-in', utils.filterObject(params), {skipAuth: true}),
  // Thu hồi token hiện tại trên server (thiết bị + site này); lỗi thì vẫn đăng xuất ở máy
  logout: () => axios.post('/auth/logout', null, {silent: true}),
  requestResetOTP: userPhone =>
    axios.post('/request-otp', {
      userPhone,
      requestType: 'reset-password'
    }),
  sendAcceptPoliciesEmail: data => axios.post('/users-permissions/sendAcceptPoliciesEmail', data),
  sendSubmitForm2Email: data => axios.post('/users-permissions/sendSubmitForm2Email', data),
  confirmResetOTP: (userPhone, otp) => axios.post(`/confirm-reset-password/${userPhone}`, {otp}),
  changePassword: (userPhone, newPassword, confirmNewPassword) =>
    axios.put(`/change-password/${userPhone}`, {
      newPassword,
      confirmNewPassword
    }),
  changeUserPassword: (id, oldPassword, password) =>
    axios.put(`/change-user-password/${id}`, {
      oldPassword,
      password
    }),
  register: (username, password, phone, email, cfToken, enrollmentGrade, name, userStudentData) =>
    axios.post('/users-permissions/regsiterAccount', {
      username,
      password,
      parentPhone: phone,
      email,
      cfToken,
      enrollmentGrade,
      name,
      studentData: userStudentData
    }),
  requestRegisterOTP: ({phone, username}) =>
    axios.post('/request-otp', {
      userPhone: phone,
      username,
      requestType: 'register'
    }),
  confirmSignupOTP: (userPhone, username, otp) =>
    axios.post('/confirm-register', {
      userPhone,
      username,
      otp
    }),
  syncUserProfile: data => axios.post('/users-permissions/syncUserProfile', data),
  updateAddressBeforePayment: data => axios.post('/users-permissions/updateAddressBeforePayment', data),
  signatureConfirmation: data => axios.post('/users-permissions/signatureConfirmation', data),
  getSignatureUploadUrl: () => axios.get('/users-permissions/getSignatureUploadUrl')
}

export const Policies = {
  ...createAPIHelper(API_ENDPOINTS.POLICIES),
  checkAuthPolicies: id => axios.get(`/checkAuthPolicies/${id}`)
}

export const CV = {
  ...createAPIHelper(API_ENDPOINTS.CV),
  create: (parent, params) => axios.post(`/cvs/${parent}`, params),
  update: (id, params) => axios.put(`/cvs/${id}`, params),
  checkDocumentSystemTime: params => axios.post('/checkDocumentSystemTime', params),
  checkSystemTime: () => axios.get('/checkSystemTime'),
  checkServerTime: () => axios.get('/checkServerTime'),
  startHourlySMSTask: () => axios.get('/startHourlySMSTask'),
  stopHourlySMSTask: () => axios.get('/stopHourlySMSTask'),
  // Deprecated - ZALO_PAY (all six below): use Transaction.*
  createPaymentUrl: data => axios.post('/createpaymenturl', data),
  createPhase2Payment: data => axios.post('/createPhase2Payment', data),
  checkPhase2Status: data => axios.post('/checkPhase2Status', data),
  checkPaymentStatus: data => axios.post('/checkpaymentstatus', data),
  createEnrollPayment: data => axios.post('/createEnrollPayment', data),
  checkEnrollPaymentStatus: data => axios.post('/checkEnrollPaymentStatus', data),
  updateResults: data => axios.post('/updateResults', data),
  checkAvailable: params => axios.get('/checkAvailable', {params: utils.filterObject(params)}),
  updateAppliedTo: data => axios.post('/updateAppliedTo', data),
  updateHighAcceptedTo: data => axios.post('/updateHighAcceptedTo', data),
  updateHighAppliedTo: data => axios.post('/updateHighAppliedTo', data),
  removeQid: data => axios.post('/removeQid', data),
  cancelForm: data => axios.post('/cancelForm', data),
  cancelCV: data => axios.post('/cancelCv', data),
  updateSubmitDocument: data => axios.post('/updateSubmitDocument', data),
  submitDocument: data => axios.post('/submitDocument', data),
  submitDocument10: data => axios.post('/submitDocument10', data),
  countCVs: params => axios.get('/countCVs', {params: utils.filterObject(params)}),
  updateHighExamGrade: data => axios.put('/updateHighExamGrade', data),
  updateManyCheckAttributes: data => axios.post('/updateManyCheckAttributes', data),
  updateCancelStatusFromExcel: data => axios.post('/updateCancelStatusFromExcel', data),
  importSendDepartment: data => axios.post('/importSendDepartment', data),
  updateFormGrade10: data => axios.post('/updateFormGrade10', data),
  updateFormGrade1: data => axios.post('/updateFormGrade1', data),
  exportExcel: (payload) => axios.post(`/cvs/excel`, payload),
  exemptSubject: (id, data) => axios.put(`/exemptSubject/${id}`, data),
  filterSubjectExempt: params => axios.get('/filterSubjectExempt', {params: utils.filterObject(params)}),
  countSubjectExempt: params => axios.get('/countSubjectExempt', {params: utils.filterObject(params)}),
  approveAdmission: (id, data) => axios.put(`/approveAdmission/${id}`, data),
  exportCancelDocument: (data) => axios.post('/exportCancelDocument', data),
  importEnrolledClass: data => axios.post('/importEnrolledClass', data),
}

// Thanh toán chuyển khoản BIDV (VietQR), thay cho ZaloPay. Pay calls return the bill:
// {id, title, qrDataURL, bankCode, accountNumber, accountName, amount, codePay, reference}
export const Transaction = {
  payApplicationFee: data => axios.post('/transactions/pay-application-fee', data),
  payEnrollSlot: data => axios.post('/transactions/pay-enroll-slot', data),
  payServiceFee: data => axios.post('/transactions/pay-service-fee', data),
  payChangeExpectation: data => axios.post('/transactions/pay-change-expectation', data),
  getStatus: (id, config = {}) => axios.get(`/transactions/status/${id}`, config),
  getMyEnrollSlot: () => axios.get('/transactions/enroll-slot'),
}

// Kế toán xác nhận phụ huynh nộp tiền mặt cho giao dịch BIDV đang chờ (quyền confirm-cash-payment:grade-N)
export const CashPayment = {
  // status: 'pending' (mặc định) | 'success' | 'canceled' | 'all'
  // -> [{id, code, type, title, amount, status, paymentMethod, reference, paidAt, receiptNo, bankRef, confirmedBy,
  //      effectError, createdAt, cv: {studentCode, name}, parent: {name, username, phone}}]
  fetchTransactions: (grade, {q, status} = {}) =>
    axios.get('/transactions/cash/pending', {params: utils.filterObject({grade, q, status})}),
  // data: {paymentMethod: 'cash'|'transfer', receiptNo?, bankRef?, paidAt?, note?, expectedAmount}
  // -> {status, paymentMethod, receiptNo, bankRef, paidAt, canceledBills, effectError}
  confirm: (id, data) => axios.post(`/transactions/${id}/confirm-cash`, data),
}

export const Incentive = {
  check: data => axios.post('/incentives/check', data),
  createUploadUrl: data => axios.post('/incentives/attachments/upload-url', data),
  review: (cvId, data) => axios.put(`/incentives/cvs/${cvId}/review`, data),
  importReferences: data => axios.post('/incentive-references/import', data),
  findReferences: params => axios.get('/incentive-references', {params: utils.filterObject(params)}),
  countReferences: params => axios.get('/incentive-references/count', {params: utils.filterObject(params)}),
  deleteReference: id => axios.delete(`/incentive-references/${id}`),
}

// Học sinh mầm non Khối 1: đối chiếu danh sách (tuyển thẳng / miễn lệ phí lớp Grape) và Ban tuyển sinh xét duyệt
export const Preschool = {
  // gọi ngầm khi phụ huynh nhập mục I, lỗi thì form tự xử lý
  check: data => axios.post('/preschool/check', data, {silent: true}),
  review: (cvId, data) => axios.put(`/preschool/cvs/${cvId}/review`, data),
}

// Nhân sự duyệt giảm lệ phí hồ sơ (quyền edit-cv); code null = gỡ giảm giá
export const ApplicationFeeDiscount = {
  // -> {applicationFeeDiscount, applicationFeePaymentStatus, applicationFeeWaivedBy}
  setForCv: (cvId, data) => axios.put(`/application-fee-discounts/cvs/${cvId}`, data),
}

// Cấu hình VAT theo loại phí (như VAT trên nhóm khoản phí của LMS)
export const InvoiceConfig = {
  fetchItems: grade => axios.get('/invoice-configs/items', {params: {grade}}),
  // {vatType: 'none'|'taxable', vatRate?: 5|8|10}
  updateItem: (type, data) => axios.put(`/invoice-configs/items/${type}`, data),
}

export const Invoice = {
  fetch: params => axios.get('/invoices', {params: utils.filterObject(params)}),
  fetchOne: id => axios.get(`/invoices/${id}`),
  updateBuyer: (id, buyer) => axios.put(`/invoices/${id}`, {buyer}),
  // Tạo nháp cho giao dịch chưa có (id giao dịch); đã có thì trả bản có sẵn
  createDraft: id => axios.post(`/invoices/${id}/draft`),
  issue: id => axios.post(`/invoices/${id}/issue`),
  // {grade, invoiceIds} -> {jobId, total, skipped, skippedBreakdown, skipDiagnostics, invoke}
  issueBatch: (grade, invoiceIds) => axios.post('/invoices/issue-batch', {grade, invoiceIds}),
  // Poll tiến độ: lỗi tự xử lý trong BatchProgressDialog (không bật alert mỗi 2 giây)
  fetchBatchJob: jobId => axios.get(`/invoices/batch/${jobId}`, {silent: true}),
  replace: (id, data) => axios.post(`/invoices/${id}/replace`, data),
  preview: (id, {rebuild = false} = {}) => axios.post(`/invoices/${id}/preview`, null, {params: rebuild ? {rebuild: 1} : {}}),
  // Link S3 có hạn tới PDF đã phát hành -> {url, expiresIn, expiresAt}; 409 khi PDF chưa về S3
  fetchPdfUrl: id => axios.get(`/invoices/${id}/pdf-url`),
  fetchPdf: id =>
    axios.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
      errorMessage: {
        status_400: 'Hoá đơn chưa phát hành nên chưa có PDF',
        status_403: 'Bạn không có quyền xem hoá đơn khối này',
        status_404: 'MISA chưa có PDF cho hoá đơn này, vui lòng thử lại sau ít phút',
        status_422: 'Hoá đơn chưa có mã tra cứu MISA hoặc cấu hình MISA chưa đủ',
        status_502: 'MISA lỗi khi tải PDF, vui lòng thử lại sau',
        default: 'Không tải được PDF hoá đơn',
      },
    }),
}

export const DirectAdmission = {
  fetchCVs: params => axios.get('/direct-admissions/cvs', {params: utils.filterObject(params)}),
  countCVs: params => axios.get('/direct-admissions/cvs/count', {params: utils.filterObject(params)}),
  fetchReviewOptions: cvId => axios.get(`/direct-admissions/cvs/${cvId}/review-options`),
  review: (cvId, data) => axios.put(`/direct-admissions/cvs/${cvId}/review`, data),
}


export const StudentQueue = {
  ...createAPIHelper(API_ENDPOINTS.STUDENT_QUEUE)
}

export const FormQueue = {
  ...createAPIHelper(API_ENDPOINTS.FORM_QUEUE)
}

export const EnrollForm = {
  ...createAPIHelper(API_ENDPOINTS.ENROLL_FORM),
  findPrimaryClassAssignments: params =>
    axios.get(`${API_ENDPOINTS.ENROLL_FORM}primary-class-assignment`, {params: utils.filterObject(params)}),
  countPrimaryClassAssignments: params =>
    axios.get(`${API_ENDPOINTS.ENROLL_FORM}primary-class-assignment/count`, {params: utils.filterObject(params)}),
  assignPrimaryClass: (id, classId) =>
    axios.put(`${API_ENDPOINTS.ENROLL_FORM}primary-class-assignment/${id}`, {classId}),
  createHigh: data => axios.post('/createHigh', data),
  updateHigh: data => axios.post('/updateHigh', data),
  confirmApp: data => axios.post('/confirmApp', data),
  getEnrollExam: data => axios.post('/getEnrollExam', data),
  getEnrollExamLogs: data => axios.post('/getEnrollExamLogs', data),
  hasForm: data => axios.post('/hasForm', data),
  printAppointmentUrl: data => axios.post('/printAppointmentUrl', data),
  sendAppointmentSlips: data => axios.post('/sendAppointmentSlips', data),
  adminSubmitDocument: data => axios.post('/adminSubmitDocument', data)

}

export const EnrollClass = {
  ...createAPIHelper(API_ENDPOINTS.ENROLL_CLASS)
}

export const StudentServices = {
  ...createAPIHelper(API_ENDPOINTS.STUDENT_SERVICES_FORM),
  updateServices6: data => axios.post('/updateServices6', data),
  updateServices10: data => axios.post('/updateServices10', data),
  adminService: data => axios.post('/adminService', data),
  adminSubject: data => axios.post('/adminSubject', data),
  generateMeetingUrl: data => axios.post('/generateMeetingUrl', data),
  getDiffReductionAmount: data => axios.post('/getDiffReductionAmount', data),
  printServiceForm: data => axios.post('/printServiceForm', data),
  printEnrollForm: data => axios.post('/printEnrollForm', data),
  printSubmittedForm01: data => axios.post('/printSubmittedForm01', data),
  printSubmittedForm02: data => axios.post('/printSubmittedForm02', data),
  printSubmitDoc: data => axios.post('/printSubmitDoc', data),
  printSubjectGroupForm: data => axios.post('/printSubjectGroupForm', data),
  confirmRefundAmount: data => axios.post('/confirmRefundAmount', data),
  printPolicies: data => axios.post('/printPolicies', data),
  retrieveStudentForms: data => axios.post('/retrieve-student-form', data),
}

export const StudentReview = {
  ...createAPIHelper(API_ENDPOINTS.STUDENT_REVIEW),
  updateReviewResult: data => axios.post('/student-reviews/update-review-score', data)
}

export const ActiveCode = {
  ...createAPIHelper(API_ENDPOINTS.ACTIVE_CODE),
  validate: code => axios.post('/active-codes/validate', {code}),
  enableActiveCode: code => axios.put(`/active-codes/enable-code/${code}`, {}),
  disableActiveCode: code => axios.put(`/active-codes/disable-code/${code}`, {})
}

export const User = {
  ...createAPIHelper(API_ENDPOINTS.USER),
  update: (id, params) => axios.put(`/users/${id}`, params),
}

export const Upload = {
  upload: formData =>
    axios.post(API_ENDPOINTS.UPLOAD, formData, {
      headers: {'Content-Type': 'multipart/form-data'}
    }),
  destroy: id => axios.delete(API_ENDPOINTS.DESTROY + id)
}

export const Notification = createAPIHelper(API_ENDPOINTS.NOTIFICATION)

export const Lead = {
  ...createAPIHelper(API_ENDPOINTS.LEAD),
  createManyLead: leads => axios.post('/createMany', {leads})
}

export const LeadProgress = {
  ...createAPIHelper(API_ENDPOINTS.LEAD_PROGRESS)
}

export const Log = {
  ...createAPIHelper(API_ENDPOINTS.LOG)
}

export const UserLog = {
  ...createAPIHelper(API_ENDPOINTS.USER_LOG)
}

export const Appointment = {
  ...createAPIHelper(API_ENDPOINTS.APPOINTMENT)
}

export const Product = {
  ...createAPIHelper(API_ENDPOINTS.PRODUCT)
}

export const ProductType = {
  ...createAPIHelper(API_ENDPOINTS.PRODUCT_TYPE)
}

export const CountStatus = {
  ...createAPIHelper(API_ENDPOINTS.STATUS_COUNT)
}

export const CountSucceed = {
  ...createAPIHelper(API_ENDPOINTS.SUCCEED_COUNT)
}

export const Role = {
  ...createAPIHelper(API_ENDPOINTS.ROLE),
  updateRole: (id, params) => axios.put(`/users-permissions/updateRole/${id}`, params),
  queryRoles: params => axios.get('/users-permissions/queryRoles', {params: utils.filterObject(params)})
}

export const Exam = {
  ...createAPIHelper(API_ENDPOINTS.EXAM),
  examList: () => axios.get('/examList'),
  getExamInformation: () => axios.get('/exam-information'),
  signUpExtra: data => axios.post('/signUpExtra', data)
}

export const Subject = {
  ...createAPIHelper(API_ENDPOINTS.SUBJECT)
}

export const Room = {
  ...createAPIHelper(API_ENDPOINTS.ROOM),
  createManyRooms: data => axios.post('/rooms/createMany', data),
  excelRooms: data => axios.post('/rooms/excelRooms', data)
}

export const Student = {
  ...createAPIHelper(API_ENDPOINTS.STUDENT),
  studentFromCode: params => axios.get('/studentFromCv', {params: utils.filterObject(params)}),
  createAllMainExam: data => axios.post('/createAllMainExam', data),
  createAllMainExam6: data => axios.post('/createAllMainExam6', data),
  assignSubjectGroup: data => axios.post('/assignSubjectGroup', data),
  assignSubjectGroupAll: data => axios.post('/assignSubjectGroupAll', data),
  parentExamRegister: data => axios.post('/parentExamRegister', data),
  // Deprecated - ZALO_PAY (both mock* calls)
  mockCreatePaymentUrl: data => axios.post('/mockCreatePaymentUrl', data),
  mockCheckPaymentstatus: data => axios.post('/mockCheckPaymentstatus', data)
}

export const StudentExam = {
  ...createAPIHelper(API_ENDPOINTS.STUDENT_EXAM),
  assignRooms: data => axios.post('/assignRooms', data),
  fileRegister: data => axios.post('/fileRegister', data),
  indexingStudent: data => axios.put('/indexingStudent', data),
  indexingEmpty: data => axios.put('/indexingEmpty', data),
  autoAssignRooms: data => axios.put('/autoAssignRooms', data),
  autoAssignEmptyRooms: data => axios.put('/autoAssignEmptyRooms', data),
  confirmExamDate: data => axios.post('/confirmExamDate', data)
}

export const Mark = {
  ...createAPIHelper(API_ENDPOINTS.MARK),
  updateGrades: data => axios.put('/updateGrades', data),
  updateStatus: data => axios.put('/updateStatus', data),
  getMarksData: params => axios.get('/getMarksData', {params: utils.filterObject(params)}),
  getMarkStatistics: params => axios.get('/marks/statistics', {params: utils.filterObject(params)}),
  getMarksExcel: params => axios.get('/getMarksExcel', {params: utils.filterObject(params)}),
  updateCodesFromExcel: data => axios.post('/updateCodesFromExcel', data),
  getAbsentExcel: params => axios.get('/getAbsentExcel', {params: utils.filterObject(params)}),
  updateAbsentExcel: data => axios.post('/updateAbsentExcel', data),
  syncMarkWithAdmissionResult: data => axios.post('/syncMarkWithAdmissionResult', data),
  updateAdmissionResultFromExcel: data => axios.post('/updateAdmissionResultFromExcel', data),
  updateAdmissionResultFromExcel10: data => axios.post('/updateAdmissionResultFromExcel10', data),
}

export const Teacher = {
  ...createAPIHelper(API_ENDPOINTS.TEACHER)
}

export const TeacherExam = {
  ...createAPIHelper(API_ENDPOINTS.TEACHER_EXAM)
}

export const MonitorExam = {
  ...createAPIHelper(API_ENDPOINTS.MONITOR_EXAM)
}

export const RoomSubject = {
  ...createAPIHelper(API_ENDPOINTS.ROOM_SUBJECT),
  assignTestCode: data => axios.put('/assignTestCode', data),
  assignRoomCode: data => axios.put('/assignRoomCode', data),
  assignDate: data => axios.put('/assignDate', data)
}

export const SubjectGroup = {
  ...createAPIHelper(API_ENDPOINTS.SUBJECT_GROUP),
  updateMany: data => axios.put('/updateManyGroups', data)
}

export const ExamRoom = {
  ...createAPIHelper(API_ENDPOINTS.EXAM_ROOM)
}

export const Indexing = {
  ...createAPIHelper(API_ENDPOINTS.INDEXING)
}

export const Sms = {
  ...createAPIHelper(API_ENDPOINTS.SMS),
  sendManyTexts: data => axios.post('/users-permissions/sendManyTexts', data)
}

export const Email = {
  ...createAPIHelper(API_ENDPOINTS.EMAIL),
  sendManyEmails: data => axios.post('/users-permissions/sendManyEmails', data)
}

export const InfoProgress = {
  ...createAPIHelper(API_ENDPOINTS.INFO_PROGRESS)
}

export const PageSetting = {
  ...createAPIHelper(API_ENDPOINTS.PAGE_SETTING),
  setStepOpenSchedule: data => axios.post('/setStepOpenSchedule', data)
}

export const News = {
  ...createAPIHelper(API_ENDPOINTS.NEWS)
}

// Cấu hình công khai của site theo Origin (multi-domain tenant). 404 nếu Origin chưa đăng ký -> silent.
export const Site = {
  config: () => axios.get('/site-config', {skipAuth: true, silent: true})
}

export const Event = {
  ...createAPIHelper(API_ENDPOINTS.EVENT),
  checkIn: (id, data) => axios.post(`${API_ENDPOINTS.EVENT}${id}/check-in`, data)
}

export const Interview = {
  ...createAPIHelper(API_ENDPOINTS.INTERVIEW),
  exportBookings: id => axios.get(`${API_ENDPOINTS.INTERVIEW}${id}/export-bookings`, {responseType: 'blob'})
}

export const InterviewCouncil = createAPIHelper(API_ENDPOINTS.INTERVIEW_COUNCIL)

export const InterviewSlot = createAPIHelper(API_ENDPOINTS.INTERVIEW_SLOT)

export const InterviewInvitation = {
  ...createAPIHelper(API_ENDPOINTS.INTERVIEW_INVITATION),
  invite: data => axios.post(`${API_ENDPOINTS.INTERVIEW_INVITATION}invite`, data),
  inviteMany: data => axios.post(`${API_ENDPOINTS.INTERVIEW_INVITATION}invite-many`, data),
  getAdminList: params =>
    axios.get(`${API_ENDPOINTS.INTERVIEW_INVITATION}admin-list`, {params: utils.filterObject(params)}),
  updateAdminResult: (id, data) => axios.put(`${API_ENDPOINTS.INTERVIEW_INVITATION}${id}/admin-result`, data),
  getMine: () => axios.get(`${API_ENDPOINTS.INTERVIEW_INVITATION}me`),
  getAvailableSlots: id => axios.get(`${API_ENDPOINTS.INTERVIEW_INVITATION}${id}/available-slots`),
  book: (id, slot) => axios.post(`${API_ENDPOINTS.INTERVIEW_INVITATION}${id}/book`, {slot})
}

export const RuleConfig = {
  ...createAPIHelper(API_ENDPOINTS.RULE_CONFIG)
}

export const RefundForm = {
  ...createAPIHelper(API_ENDPOINTS.REFUND_FORM),
}
export const PageSettingRevision = {
  ...createAPIHelper(API_ENDPOINTS.PAGE_SETTING_REVISION),
  findByPageSetting: params => 
    axios.get(`${API_ENDPOINTS.PAGE_SETTING_REVISION}findByPageSetting`, {params: utils.filterObject(params)})
}

export const PageSettingValue = {
  ...createAPIHelper(API_ENDPOINTS.PAGE_SETTING_VALUE),
}
export const UserAdmin = {
  ...createAPIHelper(API_ENDPOINTS.USER_ADMIN),
  exportExcel: (params) => axios.get('/users-admin-excel', params),
}
export const UserStat = {
  getStatActive: () => axios.get('/user-stat/active'),
  getStatEmail: () => axios.get('/user-stat/email'),
}

export const MissingStudentData = {
  getInfo: () => axios.get('/users-permissions/get-missing-sd'),
  syncInfo: (data) => axios.post('/users-permissions/sync-missing-sd', data),
}

export const DashBoardAnalytic = {
  ...createAPIHelper(API_ENDPOINTS.DASHBOARD_ANALYTIC),
  getDashboardCharts: (params) => axios.get('/dashboard-analytic-charts', {params: utils.filterObject(params)}),
  getAnnualReport: (params) => axios.get('/annual-report', {params: utils.filterObject(params)})
}

export const Counter = {
  ...createAPIHelper(API_ENDPOINTS.COUNTER),
  syncIndexCode: data => axios.post('/syncIndexCode', data)

}

export const EnrollChangeRequest = {
  ...createAPIHelper(API_ENDPOINTS.ENROLL_CHANGE_REQUEST),
  approve: data => axios.post(`${API_ENDPOINTS.ENROLL_CHANGE_REQUEST}approve`, data)
}

export const ExamRound = {
  ...createAPIHelper(API_ENDPOINTS.EXAM_ROUND),
  create: (params, options) => axios.post(API_ENDPOINTS.EXAM_ROUND, params, options),
  update: (id, params, option) => axios.put(`${API_ENDPOINTS.EXAM_ROUND}${id}`, params, option),
  setCurrent: data => axios.put(`${API_ENDPOINTS.EXAM_ROUND}set-current`, data)
}

export const StepStudentAccess = {
  ...createAPIHelper(API_ENDPOINTS.STEP_STUDENT_ACCESS),
}

export const FormFieldDefinition = {
  ...createAPIHelper(API_ENDPOINTS.FORM_FIELD_DEFINITION),
  createField: (data, option) => axios.post('/form-field-definitions/createField', data, option),
  updateField: (data, option) => axios.post(`/form-field-definitions/updateField`, data, option),
  editSection: (data) => axios.post(`${API_ENDPOINTS.FORM_FIELD_DEFINITION}editSection`, data)
}

export const ApplicationFee = {
  ...createAPIHelper(API_ENDPOINTS.APPLICATION_FEE),
}
export const MessageTemplate = {
  ...createAPIHelper(API_ENDPOINTS.MESSAGE_TEMPLATE),
  changeStatus: data => axios.post(`${API_ENDPOINTS.MESSAGE_TEMPLATE}changeStatus`, data),
  printPreview: (id, data = {}) => axios.put(`${API_ENDPOINTS.MESSAGE_TEMPLATE}print-preview/${id}`, data)
}

export const CVTransaction = {
  ...createAPIHelper(API_ENDPOINTS.CV_TRANSACTION),
}

export const AdmissionMethod = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_METHOD),
}

export const AdmissionCycle = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_CYCLE)
}

export const AdmissionFormula = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_FORMULA)
}

export const AdmissionVariable = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_VARIABLE)
}

export const AdmissonPlan = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_PLAN),
  setActive: (id, active) => axios.put(`${API_ENDPOINTS.ADMISSION_PLAN}${id}/active`, {active}),
}

export const AdmissionPassingOption = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_PASSING_OPTION)
}

export const AdmissionEvaluation = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_EVALUATION),
  syncAdmissionEvaluation: data => axios.post(`${API_ENDPOINTS.ADMISSION_EVALUATION}syncAdmissionEvaluation`, data),
  syncAdmissionPlan: data => axios.post(`${API_ENDPOINTS.ADMISSION_EVALUATION}sync-plan`, data),
  getSyncProgress: batchId => axios.get(`${API_ENDPOINTS.ADMISSION_EVALUATION}sync-progress/${batchId}`),
  exportExcel: data => axios.post(`${API_ENDPOINTS.ADMISSION_EVALUATION}export-excel`, data),
  syncAdmissionEvaluationByOption: data => axios.post(`${API_ENDPOINTS.ADMISSION_EVALUATION}sync-passing-option`, data),
  checkAdmissionResultSync: data => axios.post(`${API_ENDPOINTS.ADMISSION_EVALUATION}sync-admission-result/check`, data),
  syncAdmissionResult: data => axios.post(`${API_ENDPOINTS.ADMISSION_EVALUATION}sync-admission-result`, data),
}

export const AdmissionEvaluationBatch = {
  ...createAPIHelper(API_ENDPOINTS.ADMISSION_EVALUATION_BATCH),
}

export const Survey = {
  ...createAPIHelper(API_ENDPOINTS.SURVEY),
  targets: params => axios.get(`${API_ENDPOINTS.SURVEY}targets`, {params: utils.filterObject(params)}),
  confirm: data => axios.post(`${API_ENDPOINTS.SURVEY}confirm`, data),
}

export const StudentSurvey = {
  ...createAPIHelper(API_ENDPOINTS.STUDENT_SURVEY),
}

export const SurveyQuestion = {
  ...createAPIHelper(API_ENDPOINTS.SURVEY_QUESTION),
}
export default {
  Auth,
  CV,
  Transaction,
  Incentive,
  DirectAdmission,
  StudentQueue,
  Upload,
  User,
  ActiveCode,
  Notification,
  Lead,
  Log,
  Appointment,
  Product,
  ProductType,
  CountStatus,
  CountSucceed,
  Role,
  Policies,
  Exam,
  Subject,
  Room,
  Student,
  StudentExam,
  Mark,
  Teacher,
  TeacherExam,
  MonitorExam,
  RoomSubject,
  SubjectGroup,
  ExamRoom,
  Sms,
  InfoProgress,
  Indexing,
  StudentReview,
  EnrollForm,
  StudentServices,
  BaseGrade,
  FormQueue,
  PageSetting,
  News,
  Event,
  Interview,
  InterviewCouncil,
  InterviewSlot,
  InterviewInvitation,
  EnrollNumber,
  RuleConfig,
  RefundForm,
  PageSettingRevision,
  PageSettingValue,
  MissingStudentData,
  UserAdmin,
  UserStat,
  EnrollSlot,
  DashBoardAnalytic,
  Counter,
  EnrollChangeRequest,
  ExamRound,
  StepStudentAccess,
  EnrollmentWindow,
  FormFieldDefinition,
  ApplicationFee,
  MessageTemplate,
  CVTransaction,
  AdmissionMethod,
  AdmissionCycle,
  AdmissionFormula,
  AdmissionVariable,
  AdmissonPlan,
  AdmissionPassingOption,
  AdmissionEvaluation,
  AdmissionEvaluationBatch,
  UserLog,
  Survey,
  StudentSurvey,
  SurveyQuestion
}
